import { z } from "zod/v4";

import {
  CreateOfferDtoDescriptions,
  OfferDtoDescriptions,
} from "../descriptions/offer-dto.describe";
import { assertDescriptions } from "../descriptions/types";

import {
  CreateWriteDiscountTierDtoSchema,
  DiscountTierDtoSchema,
  WriteDiscountTierDtoSchema,
} from "./discount-tiers";
import { ShippingDiscountDtoSchema } from "./shipping-discount";
import { UserInterfaceDtoSchema } from "./user-interfaces";

export const DiscountUnitTypeList = ["unit", "dollar"] as const;
export type DiscountUnitType = (typeof DiscountUnitTypeList)[number];

export const DiscountDiscountTypeList = ["percentage", "dollar"] as const;
export type DiscountDiscountType = (typeof DiscountDiscountTypeList)[number];

export const DiscountSettingFieldList = ["unitType", "discountType"] as const;
export type DiscountSettingFieldType =
  (typeof DiscountSettingFieldList)[number];

export const DiscountApplicationMethodList = ["perItem", "perOrder"] as const;
export type DiscountApplicationMethodType =
  (typeof DiscountApplicationMethodList)[number];

// TODO: BB this is where the amount off X will go
export const DiscountApplicationTypeList = [
  "tieredDiscount",
  "cartDiscount",
  "freeGift",
  "freeShipping",
  "buyXGetY",
  "",
] as const;

export type DiscountApplicationType =
  (typeof DiscountApplicationTypeList)[number];

export type DiscountApplicationTypeNotEmpty = Exclude<
  DiscountApplicationType,
  ""
>;

export const SubscriptionApplicationTypeList = [
  "oneTime",
  "firstSub",
  "oneTimeAndFirstSub",
] as const;
export type SubscriptionApplicationType =
  (typeof SubscriptionApplicationTypeList)[number];

export const OfferLikeObjectSchema = z.object({
  id: z.uuid(),
  enabled: z.boolean().default(false),
  isTest: z.boolean().default(false),
  isArchived: z.boolean().default(false),
  name: z.string(),
  // offerProducts: OfferProductDtoSchema.array().optional().default([]),
  unitType: z.enum(DiscountUnitTypeList).default("dollar"),
  discountType: z.enum(DiscountDiscountTypeList).default("dollar"),
  tiers: DiscountTierDtoSchema.array().default([]),
  maximumDollarDiscount: z.number().nullish(),
  discountApplicationType: z.enum(DiscountApplicationTypeList),
  discountApplicationMethod: z.enum(DiscountApplicationMethodList),
  combinesWithIntelligems: z.boolean(),
  combinesWithShopify: z.boolean(),
  shouldRejectCodes: z.boolean(),
  shippingDiscount: ShippingDiscountDtoSchema.nullish(),
  requireSameItem: z.boolean().default(false),
  subscriptionApplicationType: z
    .enum(SubscriptionApplicationTypeList)
    .default("oneTime"),
});

export type OfferLikeObject = z.infer<typeof OfferLikeObjectSchema>;

export const OfferDtoSchema = z.object({
  id: z.uuid().describe(OfferDtoDescriptions.id),
  variationId: z.uuid().describe(OfferDtoDescriptions.variationId),
  enabled: z.boolean().default(false).describe(OfferDtoDescriptions.enabled),
  isTest: z.boolean().default(false).describe(OfferDtoDescriptions.isTest),
  isArchived: z
    .boolean()
    .default(false)
    .describe(OfferDtoDescriptions.isArchived),
  name: z.string().describe(OfferDtoDescriptions.name),
  // offerProducts: OfferProductDtoSchema.array().optional().default([]),
  unitType: z
    .enum(DiscountUnitTypeList)
    .default("dollar")
    .describe(OfferDtoDescriptions.unitType),
  discountType: z
    .enum(DiscountDiscountTypeList)
    .default("dollar")
    .describe(OfferDtoDescriptions.discountType),
  tiers: DiscountTierDtoSchema.array()
    .default([])
    .describe(OfferDtoDescriptions.tiers),
  maximumDollarDiscount: z.coerce
    .number()
    .nullish()
    .describe(OfferDtoDescriptions.maximumDollarDiscount),
  discountApplicationType: z
    .enum(DiscountApplicationTypeList)
    .describe(OfferDtoDescriptions.discountApplicationType),
  discountApplicationMethod: z
    .enum(DiscountApplicationMethodList)
    .describe(OfferDtoDescriptions.discountApplicationMethod),
  combinesWithIntelligems: z
    .boolean()
    .describe(OfferDtoDescriptions.combinesWithIntelligems),
  combinesWithShopify: z
    .boolean()
    .describe(OfferDtoDescriptions.combinesWithShopify),
  shouldRejectCodes: z
    .boolean()
    .describe(OfferDtoDescriptions.shouldRejectCodes),
  shippingDiscount: ShippingDiscountDtoSchema.nullable().describe(
    OfferDtoDescriptions.shippingDiscount
  ),
  requireSameItem: z
    .boolean()
    .default(false)
    .describe(OfferDtoDescriptions.requireSameItem),
  subscriptionApplicationType: z
    .enum(SubscriptionApplicationTypeList)
    .default("oneTime")
    .describe(OfferDtoDescriptions.subscriptionApplicationType),
});

export type OfferDto = z.infer<typeof OfferDtoSchema>;

// Write schema with strict tier validation - for write endpoints only
export const WriteOfferDtoSchema = OfferDtoSchema.extend({
  tiers: WriteDiscountTierDtoSchema.array()
    .default([])
    .describe(OfferDtoDescriptions.tiers),
});

// Write schema for creation - tier IDs are omitted (auto-generated by server)
export const CreateWriteOfferDtoSchema = OfferDtoSchema.extend({
  tiers: CreateWriteDiscountTierDtoSchema.array()
    .default([])
    .describe(OfferDtoDescriptions.tiers),
});

export const OfferWithIdSchema = OfferDtoSchema.extend({
  userInterfaces: UserInterfaceDtoSchema.array().optional(),
});

export type OfferWithIdType = z.infer<typeof OfferWithIdSchema>;

export const CreateOfferDtoSchema = OfferDtoSchema.extend({
  id: z.string().optional(),
  enabled: OfferDtoSchema.shape.enabled.describe(
    CreateOfferDtoDescriptions.enabled
  ),
  isTest: OfferDtoSchema.shape.isTest.describe(
    CreateOfferDtoDescriptions.isTest
  ),
  name: OfferDtoSchema.shape.name.describe(CreateOfferDtoDescriptions.name),
  unitType: OfferDtoSchema.shape.unitType.describe(
    CreateOfferDtoDescriptions.unitType
  ),
  discountType: OfferDtoSchema.shape.discountType.describe(
    CreateOfferDtoDescriptions.discountType
  ),
  maximumDollarDiscount: OfferDtoSchema.shape.maximumDollarDiscount.describe(
    CreateOfferDtoDescriptions.maximumDollarDiscount
  ),
  discountApplicationType:
    OfferDtoSchema.shape.discountApplicationType.describe(
      CreateOfferDtoDescriptions.discountApplicationType
    ),
  discountApplicationMethod:
    OfferDtoSchema.shape.discountApplicationMethod.describe(
      CreateOfferDtoDescriptions.discountApplicationMethod
    ),
  combinesWithIntelligems:
    OfferDtoSchema.shape.combinesWithIntelligems.describe(
      CreateOfferDtoDescriptions.combinesWithIntelligems
    ),
  combinesWithShopify: OfferDtoSchema.shape.combinesWithShopify.describe(
    CreateOfferDtoDescriptions.combinesWithShopify
  ),
  shouldRejectCodes: OfferDtoSchema.shape.shouldRejectCodes.describe(
    CreateOfferDtoDescriptions.shouldRejectCodes
  ),
  requireSameItem: OfferDtoSchema.shape.requireSameItem.describe(
    CreateOfferDtoDescriptions.requireSameItem
  ),
  subscriptionApplicationType:
    OfferDtoSchema.shape.subscriptionApplicationType.describe(
      CreateOfferDtoDescriptions.subscriptionApplicationType
    ),
}).omit({ tiers: true, shippingDiscount: true });

export type CreateOfferDto = z.infer<typeof CreateOfferDtoSchema>;

export const PatchOfferDtoSchema = z.object({
  id: z.uuid().optional(),
  enabled: z.boolean().default(false),
  isTest: z.boolean().default(false),
  isArchived: z.boolean().default(false),
  name: z.string(),
  unitType: z.enum(DiscountUnitTypeList).default("dollar"),
  discountType: z.enum(DiscountDiscountTypeList).default("dollar"),
  maximumDollarDiscount: z.number().nullish(),
  discountApplicationType: z.enum(DiscountApplicationTypeList),
  discountApplicationMethod: z.enum(DiscountApplicationMethodList),
  combinesWithIntelligems: z.boolean(),
  combinesWithShopify: z.boolean(),
  shouldRejectCodes: z.boolean(),
  requireSameItem: z.boolean().default(false),
  subscriptionApplicationType: z
    .enum(SubscriptionApplicationTypeList)
    .default("oneTime"),
});

export type PatchOfferDto = z.infer<typeof PatchOfferDtoSchema>;

// Compile-time check: description object must cover all schema keys
assertDescriptions<typeof OfferDtoDescriptions, OfferDto>();
